/**
 * Brick & Board — live deck stats for the "Upgrade your deck" tool (bb-du stats v1).
 * Everything here is computed in the browser from Scryfall card data (type line, mana cost, oracle text)
 * and plain hypergeometric math. No random simulation, no third-party stats service.
 * Stats chosen to cover the same ground as common Commander deck-check tools: mana curve, average mana value,
 * type mix, color pips vs. sources, land/ramp counts, land-drop odds, screw/flood odds, draw, interaction,
 * tutors, fast mana, Game Changers, extra turns and two-card combos.
 *
 * API (window.BBDeckStats):
 *   analyze(items, opts) -> stats      items: [{ card, qty, f }]  (library cards; f = role flags from the tool)
 *   fill(host, before, after, info)    paints the panel body
 *   hyper.pmf / hyper.cdf              exposed for tests
 */
(function () {
  'use strict';

  /* ---------- hypergeometric ---------- */
  var LF = [0, 0];
  function lf(n) { for (var i = LF.length; i <= n; i++) LF[i] = LF[i - 1] + Math.log(i); return LF[n]; }
  function lc(n, k) { return lf(n) - lf(k) - lf(n - k); }
  // P(X = k) drawing n cards from N containing K successes
  function pmf(N, K, n, k) {
    if (k < 0 || k > K || n - k < 0 || n - k > N - K || n > N) return 0;
    return Math.exp(lc(K, k) + lc(N - K, n - k) - lc(N, n));
  }
  function cdf(N, K, n, k) { // P(X <= k)
    var s = 0;
    for (var i = 0; i <= k; i++) s += pmf(N, K, n, i);
    return Math.max(0, Math.min(1, s));
  }
  function geq(N, K, n, k) { return k <= 0 ? 1 : 1 - cdf(N, K, n, k - 1); }

  /* ---------- card helpers ---------- */
  var COLORS = ['W', 'U', 'B', 'R', 'G'];
  var COLOR_NAME = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };
  var BASIC_COLOR = { plains: 'W', island: 'U', swamp: 'B', mountain: 'R', forest: 'G' };
  var TYPES = ['Creature', 'Instant', 'Sorcery', 'Artifact', 'Enchantment', 'Planeswalker', 'Battle', 'Land'];
  function frontType(c) { return String((c && c.type_line) || '').split('//')[0]; }
  function backType(c) { return String((c && c.type_line) || '').split('//')[1] || ''; }
  function oracle(c) {
    var t = (c && c.oracle_text) || '';
    if (c && c.card_faces) t += '\n' + c.card_faces.map(function (f) { return f.oracle_text || ''; }).join('\n');
    return t.toLowerCase();
  }
  function landKind(c) {
    if (!c) return '';
    if (/\bLand\b/.test(frontType(c))) return 'land';
    if (c.layout === 'modal_dfc' && /\bLand\b/.test(backType(c))) return 'mdfc'; // spell on the front, land on the back: can be played as a land
    return '';
  }
  function primaryType(c) {
    var t = frontType(c);
    if (/\bLand\b/.test(t)) return 'Land';
    for (var i = 0; i < TYPES.length; i++) if (new RegExp('\\b' + TYPES[i] + '\\b').test(t)) return TYPES[i];
    return 'Other';
  }
  function costOf(c) {
    var m = c.mana_cost;
    if (!m && c.card_faces && c.card_faces[0]) m = c.card_faces[0].mana_cost;
    return m || '';
  }
  // colored mana symbols in a cost: hybrid counts half to each color, Phyrexian counts as the color
  function pipsOf(c) {
    var out = { W: 0, U: 0, B: 0, R: 0, G: 0 };
    var re = /\{([^}]+)\}/g, m;
    var cost = costOf(c);
    while ((m = re.exec(cost))) {
      var cols = m[1].split('/').filter(function (p) { return COLORS.indexOf(p) !== -1; });
      if (!cols.length) continue;
      cols.forEach(function (x) { out[x] += 1 / cols.length; }); // {W}, {2/W}, {W/P} = 1; hybrid {W/U} = half each
    }
    return out;
  }
  // colors a card can make (lands, mana rocks/dorks, land-fetchers), limited to the deck's colors
  function producedColors(c, deckColors, isLand) {
    var set = Object.create(null);
    (c.produced_mana || []).forEach(function (x) { if (COLORS.indexOf(x) !== -1) set[x] = true; });
    if (isLand) {
      var o = oracle(c);
      var m = o.match(/search your library for (?:a|an|up to \w+) ([^.,;]*?)(?: cards?)(?:[ ,.]|$)/);
      if (m) {
        var what = m[1], found = false;
        Object.keys(BASIC_COLOR).forEach(function (b) { if (new RegExp('\\b' + b + '\\b').test(what)) { set[BASIC_COLOR[b]] = true; found = true; } });
        if (!found && /land/.test(what)) deckColors.forEach(function (x) { set[x] = true; }); // "basic land card": any color the deck's basics make
      }
    }
    return COLORS.filter(function (x) { return set[x] && deckColors.indexOf(x) !== -1; });
  }
  function isManaPermanent(c) {
    var t = frontType(c);
    if (/\b(Instant|Sorcery|Land)\b/.test(t)) return false;
    return (c.produced_mana || []).length > 0 && /\badd\b/.test(oracle(c));
  }

  /* ---------- analyze ---------- */
  // items: [{ card (Scryfall card or null), qty, f: {ramp,draw,removal,wipe,counter,tutor,fast,gc,protection} }]
  // opts:  { fmt, colors: 'WUBRG', commanders: [card], handSize: 7, drawFirst: bool }
  function analyze(items, opts) {
    opts = opts || {};
    var deckColors = String(opts.colors || '').split('').filter(function (x) { return COLORS.indexOf(x) !== -1; });
    var hand = opts.handSize || 7;
    var s = {
      n: 0, unknown: 0, lands: 0, mdfc: 0, landSlots: 0, nonland: 0, mvSum: 0, mvN: 0,
      curve: [0, 0, 0, 0, 0, 0, 0, 0], types: {}, pips: { W: 0, U: 0, B: 0, R: 0, G: 0 }, pipTotal: 0,
      src: { W: { land: 0, rock: 0 }, U: { land: 0, rock: 0 }, B: { land: 0, rock: 0 }, R: { land: 0, rock: 0 }, G: { land: 0, rock: 0 } },
      ramp: 0, draw: 0, removal: 0, wipe: 0, counter: 0, interaction: 0, tutor: 0, fast: 0, gc: 0,
      names: { gc: [], tutor: [], fast: [], ramp: [] }
    };
    TYPES.forEach(function (t) { s.types[t] = 0; });
    s.types.Other = 0;
    items.forEach(function (it) {
      var q = Math.max(0, it.qty | 0);
      if (!q) return;
      s.n += q;
      var c = it.card;
      if (!c) { s.unknown += q; return; }
      var f = it.f || {};
      var lk = landKind(c);
      var pt = primaryType(c);
      s.types[pt] = (s.types[pt] || 0) + q;
      if (lk === 'land') s.lands += q; else if (lk === 'mdfc') s.mdfc += q;
      if (lk) {
        producedColors(c, deckColors, true).forEach(function (x) { s.src[x].land += q; });
      }
      if (lk !== 'land') { // spells (including spell//land MDFCs): curve, MV, pips, roles
        var mv = Math.max(0, Math.floor(c.cmc || 0));
        s.curve[Math.min(7, mv)] += q; s.mvSum += (c.cmc || 0) * q; s.mvN += q; s.nonland += q;
        var p = pipsOf(c);
        COLORS.forEach(function (x) { s.pips[x] += p[x] * q; });
        if (!lk && isManaPermanent(c)) producedColors(c, deckColors, false).forEach(function (x) { s.src[x].rock += q; });
        if (f.ramp && !lk) { s.ramp += q; if (s.names.ramp.length < 60) s.names.ramp.push(c.name); }
        if (f.draw) s.draw += q;
        if (f.removal && !f.wipe) s.removal += q;
        if (f.wipe) s.wipe += q;
        if (f.counter) s.counter += q;
        if (f.removal || f.wipe || f.counter) s.interaction += q;
        if (f.tutor) { s.tutor += q; s.names.tutor.push(c.name); }
        if (f.fast) { s.fast += q; s.names.fast.push(c.name); }
      }
      if (f.gc) { s.gc += q; s.names.gc.push(c.name); }
    });
    // commander(s) are in the command zone: they add color demand but are not in the library
    (opts.commanders || []).forEach(function (c) { var p = pipsOf(c); COLORS.forEach(function (x) { s.pips[x] += p[x]; }); });
    s.pipTotal = COLORS.reduce(function (n, x) { return n + s.pips[x]; }, 0);
    s.avgMv = s.mvN ? s.mvSum / s.mvN : 0;
    s.landSlots = s.lands + s.mdfc; // cards that can be played as a land drop
    var N = s.n, K = s.landSlots;
    var seen = function (t) { return Math.min(N, hand + t - (opts.drawFirst === false ? 1 : 0)); };
    s.seen = seen;
    s.prob = { drops: {}, open: {}, miss: {}, flood: {}, color: {} };
    if (N > 0) {
      for (var t = 2; t <= 5; t++) s.prob.drops[t] = geq(N, K, seen(t), t);
      var h = Math.min(hand, N);
      s.prob.open.low = cdf(N, K, h, 2);           // 2 or fewer lands in the opening 7
      s.prob.open.keep = geq(N, K, h, 2) - geq(N, K, h, 6); // 2–5 lands
      s.prob.open.flood = geq(N, K, h, 5);          // 5 or more lands in the opening 7
      s.prob.miss[3] = 1 - s.prob.drops[3];
      s.prob.miss[4] = 1 - s.prob.drops[4];
      s.prob.flood[4] = geq(N, K, seen(4), 4 + 2);  // 2+ spare lands in hand on turn 4
      COLORS.forEach(function (x) {
        var Kc = s.src[x].land;
        s.prob.color[x] = { t3: geq(N, Kc, seen(3), 1), t5: geq(N, Kc, seen(5), 1) };
      });
      s.exp = {}; for (var te = 2; te <= 5; te++) s.exp[te] = N ? seen(te) * K / N : 0;
    }
    return s;
  }

  /* ---------- painting ---------- */
  function esc(x) { return String(x == null ? '' : x).replace(/[&<>"]/g, function (m) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]; }); }
  function num(x, d) { return (Math.round(x * Math.pow(10, d || 0)) / Math.pow(10, d || 0)).toFixed(d || 0); }
  function pc(x) { var v = x * 100; return (v >= 99.95 ? '100' : v < 0.05 ? '0' : v.toFixed(v < 10 ? 1 : 0)) + '%'; }
  // "a → b" when they differ; just "a" when equal. dir: 'up' good when it rises, 'down' good when it falls, '' neutral
  function pair(a, b, fmt, dir, hasAfter) {
    var fa = fmt(a), fb = fmt(b);
    if (!hasAfter || fa === fb) return '<span class="bb-ds__v">' + esc(fa) + '</span>';
    var up = b > a, good = dir ? ((dir === 'up') === up) : null;
    return '<span class="bb-ds__v"><span class="bb-ds__was">' + esc(fa) + '</span><span class="bb-ds__arr" aria-hidden="true">→</span><span class="bb-ds__now ' + (good == null ? '' : good ? 'is-good' : 'is-bad') + '">' + esc(fb) + '</span><span class="bb-ds__sr"> (was ' + esc(fa) + ')</span></span>';
  }
  function tile(label, a, b, fmt, dir, has, tip) {
    return '<div class="bb-ds__tile"' + (tip ? ' title="' + esc(tip) + '"' : '') + '><span class="bb-ds__tl">' + esc(label) + '</span>' + pair(a, b, fmt, dir, has) + '</div>';
  }
  function curveHtml(A, B, has) {
    var max = 1, i;
    for (i = 0; i < 8; i++) max = Math.max(max, A.curve[i], has ? B.curve[i] : 0);
    var cols = '';
    for (i = 0; i < 8; i++) {
      var a = A.curve[i], b = has ? B.curve[i] : a;
      cols += '<li class="bb-ds__col" aria-label="Mana value ' + (i === 7 ? '7 or more' : i) + ': ' + a + (has && a !== b ? ' now, ' + b + ' with upgrades' : '') + '">' +
        '<span class="bb-ds__bars"><span class="bb-ds__bar bb-ds__bar--a" style="height:' + Math.round(a / max * 100) + '%"><i>' + (a || '') + '</i></span>' +
        (has ? '<span class="bb-ds__bar bb-ds__bar--b" style="height:' + Math.round(b / max * 100) + '%"><i>' + (b !== a ? (b || '0') : '') + '</i></span>' : '') + '</span>' +
        '<span class="bb-ds__mv">' + (i === 7 ? '7+' : i) + '</span></li>';
    }
    return '<ul class="bb-ds__curve" role="img" aria-label="Mana curve">' + cols + '</ul>';
  }
  function typesHtml(A, B, has) {
    var max = 1; TYPES.concat(['Other']).forEach(function (t) { max = Math.max(max, A.types[t] || 0, has ? B.types[t] || 0 : 0); });
    return '<ul class="bb-ds__rows">' + TYPES.concat(['Other']).filter(function (t) { return (A.types[t] || 0) || (has && (B.types[t] || 0)); }).map(function (t) {
      var a = A.types[t] || 0, b = has ? (B.types[t] || 0) : a;
      return '<li class="bb-ds__row"><span class="bb-ds__rl">' + esc(t === 'Planeswalker' ? 'Planeswalkers' : t === 'Land' ? 'Lands' : t === 'Other' ? 'Other' : t === 'Sorcery' ? 'Sorceries' : t + 's') + '</span><span class="bb-ds__rbar"><i style="width:' + Math.round((has ? b : a) / max * 100) + '%"></i></span>' + pair(a, b, function (x) { return String(x); }, '', has) + '</li>';
    }).join('') + '</ul>';
  }
  function colorsHtml(A, B, has, colors) {
    var list = COLORS.filter(function (x) { return colors.indexOf(x) !== -1 || A.pips[x] > 0 || (has && B.pips[x] > 0); });
    if (!list.length) return '<p class="bb-ds__muted">A colorless deck: no colored mana needed.</p>';
    return '<ul class="bb-ds__colors">' + list.map(function (x) {
      var pa = A.pipTotal ? A.pips[x] / A.pipTotal : 0, pb = B.pipTotal ? B.pips[x] / B.pipTotal : 0;
      var la = A.landSlots ? A.src[x].land / A.landSlots : 0, lb = B.landSlots ? B.src[x].land / B.landSlots : 0;
      var shP = has ? pb : pa, shL = has ? lb : la;
      return '<li class="bb-ds__color"><span class="bb-ds__chip bb-ds__chip--' + x + '" title="' + COLOR_NAME[x] + '">' + x + '</span><div class="bb-ds__cbody">' +
        '<div class="bb-ds__cline"><span class="bb-ds__cl">Pips needed</span><span class="bb-ds__mbar"><i style="width:' + Math.min(100, Math.round(shP * 100)) + '%"></i></span>' + pair(A.pips[x], B.pips[x], function (v) { return num(v, v % 1 ? 1 : 0); }, '', has) + '<span class="bb-ds__pct">' + Math.round(shP * 100) + '%</span></div>' +
        '<div class="bb-ds__cline"><span class="bb-ds__cl">Lands making it</span><span class="bb-ds__mbar bb-ds__mbar--src"><i style="width:' + Math.min(100, Math.round(shL * 100)) + '%"></i></span>' + pair(A.src[x].land, B.src[x].land, function (v) { return String(v); }, 'up', has) + '<span class="bb-ds__pct">' + Math.round(shL * 100) + '%</span></div>' +
        '<div class="bb-ds__cmeta">+ ' + pair(A.src[x].rock, B.src[x].rock, function (v) { return String(v); }, '', has) + ' rocks/dorks · ' + (has ? '' : '') + '≥1 source by turn 3: ' + pair(A.prob.color[x] ? A.prob.color[x].t3 : 0, B.prob.color[x] ? B.prob.color[x].t3 : 0, pc, 'up', has) + '</div>' +
        '</div></li>';
    }).join('') + '</ul>';
  }
  function dropsHtml(A, B, has) {
    if (!A.n) return '';
    var rows = '';
    for (var t = 2; t <= 5; t++) rows += '<li class="bb-ds__row"><span class="bb-ds__rl">Turn ' + t + '</span><span class="bb-ds__rbar bb-ds__rbar--p"><i style="width:' + Math.round((has ? B : A).prob.drops[t] * 100) + '%"></i></span>' + pair(A.prob.drops[t], B.prob.drops[t], pc, 'up', has) + '</li>';
    return '<ul class="bb-ds__rows">' + rows + '</ul>';
  }
  function riskHtml(A, B, has) {
    function r(label, a, b, dir, tip) { return '<li class="bb-ds__row bb-ds__row--r" title="' + esc(tip || '') + '"><span class="bb-ds__rl">' + esc(label) + '</span><span class="bb-ds__rbar bb-ds__rbar--r"><i style="width:' + Math.round(Math.min(1, (has ? b : a)) * 100) + '%"></i></span>' + pair(a, b, pc, dir, has) + '</li>'; }
    return '<ul class="bb-ds__rows">' +
      r('2 or fewer lands in opening 7', A.prob.open.low, B.prob.open.low, 'down', 'Screw risk: a hand with 0–2 lands') +
      r('Miss a land drop by turn 3', A.prob.miss[3], B.prob.miss[3], 'down', 'Fewer than 3 lands among the first 10 cards') +
      r('Miss a land drop by turn 4', A.prob.miss[4], B.prob.miss[4], 'down', 'Fewer than 4 lands among the first 11 cards') +
      r('5+ lands in opening 7', A.prob.open.flood, B.prob.open.flood, 'down', 'Flood risk: a hand that is mostly lands') +
      r('2+ spare lands in hand on turn 4', A.prob.flood[4], B.prob.flood[4], 'down', '6 or more lands among the first 11 cards (4 played, 2+ left over)') +
      r('Keepable opener (2–5 lands)', A.prob.open.keep, B.prob.open.keep, 'up', 'Chance your first 7 has 2 to 5 lands') +
      '</ul>';
  }
  function edhHtml(A, B, has, info) {
    var rows = [];
    function add(label, a, b, dir, tip) { rows.push('<li class="bb-ds__row bb-ds__row--t" title="' + esc(tip || '') + '"><span class="bb-ds__rl">' + esc(label) + '</span>' + pair(a, b, String, dir, has) + '</li>'); }
    add('Game Changers', A.gc, B.gc, '', (B.names.gc.length ? B.names.gc : A.names.gc).join(', '));
    add('Tutors', A.tutor, B.tutor, '', (B.names.tutor.length ? B.names.tutor : A.names.tutor).slice(0, 12).join(', '));
    add('Fast mana', A.fast, B.fast, '', (B.names.fast.length ? B.names.fast : A.names.fast).join(', '));
    if (A.edh) {
      add('Extra-turn cards', A.edh.xt, B.edh ? B.edh.xt : A.edh.xt, '', '');
      add('Two-card combos', A.edh.combos, B.edh ? B.edh.combos : A.edh.combos, '', '');
      if (A.edh.mld || (B.edh && B.edh.mld)) add('Mass land denial', A.edh.mld, B.edh ? B.edh.mld : A.edh.mld, '', '');
      rows.push('<li class="bb-ds__row bb-ds__row--t" title="Rough WotC bracket estimate from Game Changers, combos and extra turns"><span class="bb-ds__rl">Bracket estimate</span>' +
        pair(A.edh.est, B.edh ? B.edh.est : A.edh.est, function (x) { return x >= 4 ? '4–5' : x === 3 ? '3' : '1–2'; }, '', has) + '</li>');
    }
    return '<ul class="bb-ds__rows bb-ds__rows--t">' + rows.join('') + '</ul>';
  }

  // headline shown while the panel is collapsed
  function headline(A, B, has) {
    function p(a, b, f) { var fa = f(a), fb = f(b); return has && fa !== fb ? fa + '→' + fb : fa; }
    return 'Avg MV ' + p(A.avgMv, B.avgMv, function (x) { return num(x, 2); }) + ' · Lands ' + p(A.landSlots, B.landSlots, String) + ' · Ramp ' + p(A.ramp, B.ramp, String) +
      ' · Screw (≤2 in 7) ' + p(A.prob.open.low, B.prob.open.low, pc);
  }

  function fill(host, A, B, info) {
    info = info || {};
    var has = !!B && !!info.hasChange;
    if (!B) B = A;
    if (!A.n) { host.innerHTML = '<p class="bb-ds__muted">Not enough cards to run the numbers.</p>'; return ''; }
    var lede;
    if (info.picks) lede = 'Your deck <b>→</b> with the <b>' + info.picks + '</b> upgrade' + (info.picks === 1 ? '' : 's') + ' you have checked' + (info.cuts ? ', swapping out <b>' + info.cuts + '</b> suggested card' + (info.cuts === 1 ? '' : 's') : '') + '.';
    else lede = 'Check upgrades below to see how your deck changes.';
    var shortNote = info.short ? '<p class="bb-ds__muted">' + info.short + ' upgrade' + (info.short === 1 ? ' has' : 's have') + ' no clear card to cut, so the deck shows ' + B.n + ' cards. Your call on what goes.</p>' : '';
    var cmdrNote = info.libNote ? '<p class="bb-ds__muted">' + esc(info.libNote) + '</p>' : '';
    var legend = has ? '<p class="bb-ds__legend"><span><i class="bb-ds__sw bb-ds__sw--a"></i>Now</span><span><i class="bb-ds__sw bb-ds__sw--b"></i>With upgrades</span></p>' : '';
    var interactionTip = 'Removal ' + B.removal + ' · Board wipes ' + B.wipe + ' · Counterspells ' + B.counter;
    host.innerHTML =
      '<p class="bb-ds__lede">' + lede + '</p>' + shortNote + cmdrNote +
      '<div class="bb-ds__tiles">' +
        tile('Cards in library', A.n, B.n, String, '', has, info.fmt === 'commander' ? 'Your 100 minus the commander' : '') +
        tile('Avg mana value', A.avgMv, B.avgMv, function (x) { return num(x, 2); }, '', has, 'Average of the nonland cards') +
        tile('Lands', A.landSlots, B.landSlots, String, '', has, A.mdfc || B.mdfc ? 'Includes ' + B.mdfc + ' spell//land double-faced card' + (B.mdfc === 1 ? '' : 's') : '') +
        tile('Ramp', A.ramp, B.ramp, String, '', has, 'Mana rocks, dorks and land-fetching spells') +
        tile('Card draw', A.draw, B.draw, String, '', has, '') +
        tile('Interaction', A.interaction, B.interaction, String, '', has, interactionTip) +
      '</div>' + legend +
      '<div class="bb-ds__grid">' +
        '<section class="bb-ds__sec"><h4>Mana curve</h4>' + curveHtml(A, B, has) + '</section>' +
        '<section class="bb-ds__sec"><h4>Card types</h4>' + typesHtml(A, B, has) + '</section>' +
        '<section class="bb-ds__sec"><h4>Color pips vs. mana sources</h4>' + colorsHtml(A, B, has, info.colors || []) + '</section>' +
        '<section class="bb-ds__sec"><h4>Chance to hit your land drop</h4>' + dropsHtml(A, B, has) + '</section>' +
        '<section class="bb-ds__sec"><h4>Mana screw and flood</h4>' + riskHtml(A, B, has) + '</section>' +
        '<section class="bb-ds__sec"><h4>' + (info.fmt === 'commander' ? 'Commander power markers' : 'Power markers') + '</h4>' + edhHtml(A, B, has, info) + '</section>' +
      '</div>' +
      '<p class="bb-ds__foot">Odds are exact (hypergeometric) for ' + (info.fmt === 'commander' ? 'a ' + B.n + '-card library, on the draw (you draw on turn 1 in multiplayer), no mulligans' : 'a ' + B.n + '-card library, on the play, no mulligans') + '. Colors count the commander’s pips. ' + (A.unknown || B.unknown ? (B.unknown || A.unknown) + ' unidentified card' + ((B.unknown || A.unknown) === 1 ? ' is' : 's are') + ' counted as spells.' : '') + '</p>';
    return headline(A, B, has);
  }

  window.BBDeckStats = { analyze: analyze, fill: fill, headline: headline, hyper: { pmf: pmf, cdf: cdf, geq: geq }, util: { pipsOf: pipsOf, landKind: landKind, producedColors: producedColors, primaryType: primaryType } };
})();
